import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Badge, Button, trackMetaStartTrial, gtagEvent, vars, formatCurrency } from '@agendor/ui';
import { Modal } from '../components/Modal';
import BusinessService, {
    BusinessDetails,
    PlanType,
    SimulationResult,
    PendingTransitionExistsError,
    hasConnectorAccess,
    hasPaidAccess,
} from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import { Plain, PlanCatalogue } from '@agendor/shared';
import { daysFromToday } from '../date-utils';

function formatDate(date: Plain.SimpleDate): string {
    return `${String(date.day).padStart(2, '0')}/${String(date.month).padStart(2, '0')}/${date.year}`;
}

// Card styles matching ConfigurationSection
const ConfigCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const ConfigInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ConfigTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ConfigSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #666;
    font-size: 0.9rem;
`;

const SummaryItem = styled.span`
    color: #666;
`;

// Modal content styles

const ModalSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    line-height: 1.5;
`;

const PriceValue = styled.span`
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const PendingBox = styled.div`
    padding: 16px;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    background-color: ${vars.warningTintSoft};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PendingTitle = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
`;

const PendingDescription = styled.div`
    font-size: 0.9rem;
    color: #666;
`;

const PendingActions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const PlanOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const PlanCard = styled.button<{ selected: boolean }>`
    width: 100%;
    padding: 16px;
    border: 1px solid ${(p) => (p.selected ? vars.foregroundColor : vars.dividerColor)};
    border-radius: 8px;
    cursor: pointer;
    background-color: ${(p) => (p.selected ? vars.primaryColorLight : 'transparent')};
    text-align: left;
    font: inherit;
    display: flex;
    align-items: center;
    gap: 12px;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.15s;

    @media (max-width: 600px) {
        flex-wrap: wrap;
    }

    &:hover:not(:disabled) {
        border-color: ${vars.foregroundColor};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    input[type='radio'] {
        margin: 0;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
`;

const PlanCardInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PlanCardName = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PlanCardDescription = styled.div`
    font-size: 0.9rem;
    color: #888;
`;

/* The paid tiers share one card: the WhatsApp features listed once, then a row per tier. */
const PlanGroup = styled.div`
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    overflow: hidden;
`;

const PlanGroupHeader = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PlanRow = styled.button<{ selected: boolean }>`
    width: 100%;
    min-height: 44px;
    padding: 12px 16px;
    border: none;
    border-top: 1px solid ${vars.dividerColor};
    cursor: pointer;
    background-color: ${(p) => (p.selected ? vars.primaryColorLight : 'transparent')};
    color: inherit;
    text-align: left;
    font: inherit;
    display: flex;
    align-items: center;
    gap: 12px;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.15s;

    &:hover:not(:disabled) {
        background-color: ${(p) => (p.selected ? vars.primaryColorLight : vars.backgroundColorBright)};
    }

    input[type='radio'] {
        margin: 0;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
`;

const PlanRowHeading = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
`;

const Footnote = styled.small`
    display: block;
    margin-top: 12px;
    color: #666;
    font-size: 0.8rem;
    line-height: 1.5;
`;

const SimulationBox = styled.div<{ variant?: 'upgrade' | 'downgrade' }>`
    padding: 16px;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    background-color: ${(p) => (p.variant === 'downgrade' ? vars.warningTintSoft : vars.successTintSoft)};
    margin-top: 16px;
`;

const SimulationTitle = styled.div`
    font-weight: 600;
    margin-bottom: 8px;
    color: ${vars.foregroundColor};
`;

const SimulationDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
`;

type SubscriptionSectionProps = {
    business: BusinessDetails | null;
    service: BusinessService | null;
    onReloadBusiness: () => Promise<void> | void;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
};

export default function SubscriptionSection({
    business,
    service,
    onReloadBusiness,
    isActive,
    modalSegments,
    onNavigate,
}: SubscriptionSectionProps) {
    const [selectedPlanType, setSelectedPlanType] = useState<PlanType>('free');
    const [simulation, setSimulation] = useState<SimulationResult | null>(null);
    const [isSimulating, setIsSimulating] = useState(false);
    const [isChanging, setIsChanging] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRemovingCard, setIsRemovingCard] = useState(false);
    const [showChangePlan, setShowChangePlan] = useState(false);

    const { showToast } = useToast();

    const { isOpen, open, close } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'plan',
    });

    const subscription = business?.subscription;
    const currentPlanType = subscription?.planType ?? 'free';
    const currentPlan = PlanCatalogue.PLANS[currentPlanType];
    const pendingTransition = subscription?.pendingTransition;
    const isInactive = business !== null && !hasPaidAccess(business);
    const isCancellation = selectedPlanType === 'free';

    // Check for upgrade query param and apply it
    const applyUpgradeParam = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        const upgradeParam = params.get('upgrade');

        if (upgradeParam !== null && PlanCatalogue.isPlanId(upgradeParam) && PlanCatalogue.isPaid(upgradeParam)) {
            setSelectedPlanType(upgradeParam);
            setShowChangePlan(true);
            params.delete('upgrade');
            const newUrl = params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            return true;
        }
        return false;
    }, []);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen && subscription) {
            setSimulation(null);

            if (!applyUpgradeParam()) {
                setShowChangePlan(false);
                setSelectedPlanType(currentPlanType);
            }
        }
    }, [isOpen, subscription, currentPlanType, applyUpgradeParam]);

    // Listen for popstate to catch upgrade param changes while modal is open
    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            applyUpgradeParam();
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isOpen, applyUpgradeParam]);

    // A subscription sold before workers became unlimited is not one of the plans on offer:
    // every tier is a change, its own included.
    const legacyWorkersLimit = business?.legacyWorkersLimit;
    const legacyContract = legacyWorkersLimit !== undefined;
    const isSamePlan = !legacyContract && selectedPlanType === currentPlanType;

    const selectPlan = (planType: PlanType) => {
        gtagEvent('subscription_plan_select', { label: planType });
        setSelectedPlanType(planType);
        setSimulation(null);
    };

    const handleSimulate = async () => {
        if (!service || isSamePlan) return;
        gtagEvent('subscription_simulate_click');

        setIsSimulating(true);
        try {
            const result = await service.simulatePlanTransition({ type: selectedPlanType });
            setSimulation(result);
        } catch (error) {
            if (error instanceof PendingTransitionExistsError) {
                showToast('Você já tem uma transição pendente. Cancele-a antes de fazer outra.', 'error', 'modal');
            } else {
                console.error('Failed to simulate plan transition', error);
                showToast('Não foi possível simular a mudança de plano.', 'error', 'modal');
            }
        } finally {
            setIsSimulating(false);
        }
    };

    const handleConfirmChange = async () => {
        if (!service || isSamePlan) return;
        gtagEvent('subscription_confirm_click');

        setIsChanging(true);
        try {
            const eventId = crypto.randomUUID();
            const result = await service.changePlan({ type: selectedPlanType }, { eventId });

            if (result.type === 'upgrade_as_trial') {
                trackMetaStartTrial({
                    phoneNumber: business?.owner.phoneNumber,
                    eventId,
                });
            }

            let message = '';
            switch (result.type) {
                case 'upgrade_immediate':
                    message = 'Plano alterado com sucesso!';
                    break;
                case 'upgrade_pending':
                    message = 'Cobrança gerada! Seu plano será alterado após o pagamento.';
                    break;
                case 'upgrade_as_trial':
                    message = `Teste grátis ativado! Válido até ${formatDate(result.trialEndsAt)}.`;
                    break;
                case 'trial_plan_changed':
                    message = isCancellation
                        ? 'Teste grátis encerrado.'
                        : `Plano alterado! Sua primeira cobrança em ${formatDate(result.trialEndsAt)} será de ${PlanCatalogue.formatPrice(result.newPriceInCents)}.`;
                    break;
                case 'downgrade_scheduled':
                    message = isCancellation
                        ? `Assinatura cancelada a partir de ${formatDate(result.effectiveDate)}.`
                        : `Downgrade programado para ${formatDate(result.effectiveDate)}.`;
                    break;
                case 'same_plan':
                    message = 'Você já está neste plano.';
                    break;
            }

            showToast(message, 'success', 'app');
            await onReloadBusiness();
            close();
        } catch (error) {
            if (error instanceof PendingTransitionExistsError) {
                showToast('Você já tem uma transição pendente. Cancele-a antes de fazer outra.', 'error', 'modal');
            } else {
                console.error('Failed to change plan', error);
                showToast('Não foi possível alterar o plano.', 'error', 'modal');
            }
        } finally {
            setIsChanging(false);
        }
    };

    const handleCancelPendingTransition = async () => {
        if (!service) return;
        gtagEvent('subscription_cancel_transition_click');

        setIsCancelling(true);
        try {
            await service.cancelPendingTransition();
            showToast('Transição cancelada.', 'success', 'modal');
            await onReloadBusiness();
        } catch (error) {
            console.error('Failed to cancel pending transition', error);
            showToast('Não foi possível cancelar a transição.', 'error', 'modal');
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRemoveCreditCard = async () => {
        if (!service) return;

        setIsRemovingCard(true);
        try {
            await service.removeCreditCard();
            showToast('Cartão removido. A próxima cobrança será enviada por link.', 'success', 'modal');
            await onReloadBusiness();
        } catch (error) {
            console.error('Failed to remove credit card', error);
            showToast('Não foi possível remover o cartão.', 'error', 'modal');
        } finally {
            setIsRemovingCard(false);
        }
    };

    const handleStartChangePlan = () => {
        if (subscription?.invoiceUrl && !subscription.isTrial) {
            showToast('Você tem uma cobrança em aberto. Pague-a antes de mudar de plano.', 'error', 'modal');
            return;
        }
        gtagEvent('subscription_change_plan_click');
        setShowChangePlan(true);
        setSimulation(null);
    };

    const handleBackToDetails = () => {
        gtagEvent('subscription_back_click');
        setShowChangePlan(false);
        setSimulation(null);
    };

    const getSummaryItems = (): string[] => {
        if (!subscription) return [];

        const items: string[] = [];

        if (isInactive) {
            items.push('Assinatura inativa');
            return items;
        }

        if (subscription.isTrial) {
            items.push(`Plano: ${currentPlan.name} (teste grátis)`);
        } else if (currentPlanType === 'free') {
            items.push('Plano: cortesia');
        } else {
            items.push(`Plano: ${currentPlan.name}`);
        }

        if (currentPlan.includedCredits !== null) {
            items.push(`Atendimentos: ~${currentPlan.approximateConversationsPerMonth.toLocaleString('pt-BR')}/mês`);
        }

        // Pending transition
        if (pendingTransition) {
            const planName = PlanCatalogue.PLANS[pendingTransition.planType].name;
            if (pendingTransition.type === 'pending_upgrade') {
                items.push(`⏳ Upgrade para ${planName} pendente`);
            } else if (pendingTransition.effectiveDate && pendingTransition.planType === 'free') {
                items.push(`📅 Cancelamento em ${formatDate(pendingTransition.effectiveDate)}`);
            } else if (pendingTransition.effectiveDate) {
                items.push(`📅 Downgrade para ${planName} em ${formatDate(pendingTransition.effectiveDate)}`);
            }
        } else if (currentPlanType !== 'free') {
            items.push(`Próxima cobrança: ${formatDate(subscription.nextBillingDate)}`);
            if (subscription.creditCard) {
                items.push(`Cartão: ${subscription.creditCard.brand} final ${subscription.creditCard.last4}`);
            }
        }

        return items;
    };

    const renderSimulationResult = () => {
        if (!simulation || simulation.type === 'same_plan') return null;

        const isDowngrade = simulation.type === 'downgrade_scheduled';

        return (
            <SimulationBox variant={isDowngrade ? 'downgrade' : 'upgrade'} data-testid="plan-simulation">
                <SimulationTitle>{isDowngrade ? '📅' : '✨'} O que vai acontecer</SimulationTitle>
                <SimulationDetails>
                    {isCancellation ? (
                        <span>
                            Seus clientes deixam de agendar pelo link e de receber lembretes. Você continua cuidando da
                            agenda por aqui.
                        </span>
                    ) : (
                        <span>
                            Novo valor mensal:{' '}
                            <strong>{PlanCatalogue.formatPrice(simulation.newPriceInCents)}/mês</strong>
                        </span>
                    )}
                    {simulation.type === 'trial_plan_changed' && (
                        <span>
                            {isCancellation ? (
                                <>Seu teste grátis será encerrado imediatamente.</>
                            ) : (
                                <>
                                    Você continua no teste grátis até{' '}
                                    <strong>{formatDate(simulation.trialEndsAt)}</strong> sem custo. A partir daí, será
                                    cobrado <strong>{PlanCatalogue.formatPrice(simulation.newPriceInCents)}/mês</strong>
                                    .
                                </>
                            )}
                        </span>
                    )}
                    {simulation.type === 'upgrade_pending' && (
                        <>
                            {currentPlanType === 'free' ? (
                                <span>
                                    Será gerada uma cobrança de{' '}
                                    <strong>{formatCurrency(simulation.proRataAmountInCents)}</strong> referente ao
                                    primeiro mês, que começa no dia do pagamento.
                                </span>
                            ) : (
                                <span>
                                    Será gerada uma cobrança de{' '}
                                    <strong>{formatCurrency(simulation.proRataAmountInCents)}</strong> referente aos
                                    dias restantes do ciclo atual.
                                </span>
                            )}
                            <span>O plano será alterado após o pagamento.</span>
                        </>
                    )}
                    {simulation.type === 'upgrade_as_trial' && (
                        <span>
                            Teste grátis até <strong>{formatDate(simulation.trialEndsAt)}</strong>. Depois, será cobrado
                            normalmente.
                        </span>
                    )}
                    {simulation.type === 'downgrade_scheduled' && (
                        <span>
                            {isCancellation ? 'A assinatura será cancelada' : 'A mudança será aplicada'} em{' '}
                            <strong>{formatDate(simulation.effectiveDate)}</strong>, no próximo ciclo de cobrança.
                        </span>
                    )}
                    {simulation.type === 'upgrade_immediate' && <span>A mudança será aplicada imediatamente.</span>}
                </SimulationDetails>
            </SimulationBox>
        );
    };

    const renderDetailsView = () => {
        if (!subscription) return null;
        return (
            <ModalSection>
                {isInactive ? (
                    <InfoText data-testid="plan-inactive-summary">
                        Sua assinatura está inativa. Seus clientes não conseguem agendar pelo link e não recebem
                        lembretes; você continua cuidando da agenda por aqui.
                    </InfoText>
                ) : (
                    <InfoText data-testid="plan-details-summary">
                        Você está no plano <strong>{currentPlan.name}</strong>
                        {subscription.isTrial && <> (teste grátis)</>}
                        {currentPlanType !== 'free' && (
                            <>
                                , por <strong>{PlanCatalogue.formatPrice(subscription.monthlyPriceInCents)}/mês</strong>
                            </>
                        )}
                        .
                        {legacyContract ? (
                            <>
                                {' '}
                                Contrato anterior: até {legacyWorkersLimit}{' '}
                                {legacyWorkersLimit === 1 ? 'profissional ativo' : 'profissionais ativos'} e
                                atendimentos por IA sem limite.
                            </>
                        ) : (
                            currentPlan.includedCredits !== null && (
                                <>
                                    {' '}
                                    Inclui cerca de{' '}
                                    {currentPlan.approximateConversationsPerMonth.toLocaleString('pt-BR')} atendimentos
                                    por mês.
                                </>
                            )
                        )}
                    </InfoText>
                )}

                {subscription.isTrial &&
                    (() => {
                        const remainingDays = daysFromToday(subscription.nextBillingDate);
                        return (
                            <InfoText>
                                {remainingDays > 1 ? (
                                    <>
                                        Restam <strong>{remainingDays} dias</strong> de teste grátis.
                                    </>
                                ) : remainingDays === 1 ? (
                                    <>
                                        Resta <strong>1 dia</strong> de teste grátis.
                                    </>
                                ) : (
                                    <>
                                        Seu teste grátis termina <strong>hoje</strong>.
                                    </>
                                )}
                            </InfoText>
                        );
                    })()}

                {currentPlanType !== 'free' && !subscription.isTrial && (
                    <InfoText>
                        Próxima cobrança em <strong>{formatDate(subscription.nextBillingDate)}</strong>.
                    </InfoText>
                )}

                {pendingTransition && (
                    <PendingBox>
                        <PendingTitle>
                            {pendingTransition.type === 'pending_upgrade'
                                ? '⏳ Upgrade pendente'
                                : pendingTransition.planType === 'free'
                                  ? '📅 Cancelamento programado'
                                  : '📅 Downgrade programado'}
                        </PendingTitle>
                        <PendingDescription>
                            {pendingTransition.planType !== 'free' && (
                                <>
                                    Novo plano: <strong>{PlanCatalogue.PLANS[pendingTransition.planType].name}</strong>
                                    <br />
                                </>
                            )}
                            {pendingTransition.type === 'pending_upgrade' && (
                                <>
                                    {currentPlanType === 'free'
                                        ? 'Aguardando pagamento para ativar.'
                                        : 'Aguardando pagamento do pro-rata para ativar.'}
                                </>
                            )}
                            {pendingTransition.type === 'scheduled_downgrade' && pendingTransition.effectiveDate && (
                                <>
                                    Será aplicado em: <strong>{formatDate(pendingTransition.effectiveDate)}</strong>
                                </>
                            )}
                        </PendingDescription>
                        <PendingActions>
                            {pendingTransition.type === 'pending_upgrade' && pendingTransition.invoiceUrl && (
                                <Button
                                    as="a"
                                    href={pendingTransition.invoiceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    primary
                                    onClick={() => gtagEvent('subscription_pay_invoice_click')}
                                >
                                    Pagar cobrança
                                </Button>
                            )}
                            <Button onClick={handleCancelPendingTransition} disabled={isCancelling} danger>
                                {isCancelling ? 'Cancelando...' : 'Cancelar transição'}
                            </Button>
                        </PendingActions>
                    </PendingBox>
                )}

                {subscription.creditCard && (
                    <PendingBox style={{ backgroundColor: 'transparent' }}>
                        <PendingTitle>Cobrança automática ativa</PendingTitle>
                        <PendingDescription>
                            Cartão {subscription.creditCard.brand} final {subscription.creditCard.last4}
                        </PendingDescription>
                        <PendingActions>
                            <Button onClick={handleRemoveCreditCard} disabled={isRemovingCard} danger>
                                {isRemovingCard ? 'Removendo...' : 'Remover cartão'}
                            </Button>
                        </PendingActions>
                    </PendingBox>
                )}

                {!subscription.creditCard && currentPlanType !== 'free' && (
                    <InfoText style={{ color: '#888', fontSize: '0.85rem' }}>
                        Para ativar a cobrança automática, pague a próxima cobrança com cartão de crédito.
                    </InfoText>
                )}

                {subscription.invoiceUrl && !pendingTransition && (
                    <div>
                        <Button
                            as="a"
                            href={subscription.invoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => gtagEvent('subscription_view_invoice_click')}
                        >
                            Ver cobrança em aberto
                        </Button>
                    </div>
                )}
            </ModalSection>
        );
    };

    const renderCancelCard = () => (
        <PlanCard
            type="button"
            data-testid="plan-card-free"
            selected={isCancellation}
            onClick={() => selectPlan('free')}
        >
            <input type="radio" name="plan" checked={isCancellation} readOnly />
            <PlanCardInfo>
                <PlanCardName>Cancelar assinatura</PlanCardName>
                <PlanCardDescription>
                    Seus clientes deixam de agendar pelo link e de receber lembretes
                </PlanCardDescription>
                <PlanCardDescription>Você continua cuidando da agenda por aqui</PlanCardDescription>
            </PlanCardInfo>
        </PlanCard>
    );

    const renderTierRow = (plan: PlanCatalogue.PaidPlan) => (
        <PlanRow
            key={plan.id}
            type="button"
            data-testid={`plan-card-${plan.id}`}
            selected={selectedPlanType === plan.id}
            onClick={() => selectPlan(plan.id)}
        >
            <input type="radio" name="plan" checked={selectedPlanType === plan.id} readOnly />
            <PlanCardInfo>
                <PlanRowHeading>
                    <PlanCardName>
                        {plan.name}
                        {!legacyContract && currentPlanType === plan.id && <Badge>Atual</Badge>}
                    </PlanCardName>
                    <PriceValue>{PlanCatalogue.formatPrice(plan.priceInCents)}/mês</PriceValue>
                </PlanRowHeading>
                <PlanCardDescription>
                    ~{plan.approximateConversationsPerMonth.toLocaleString('pt-BR')} atendimentos/mês ·{' '}
                    {plan.recommendedFor}
                </PlanCardDescription>
            </PlanCardInfo>
        </PlanRow>
    );

    const renderChangePlanView = () => (
        <ModalSection>
            {legacyContract && (
                <InfoText data-testid="plan-legacy-note">
                    Seu plano atual é um contrato anterior aos planos abaixo. Ao alterar, você passa para as condições
                    atuais — profissionais ilimitados e uma franquia de atendimentos por mês — inclusive ao escolher o{' '}
                    {currentPlan.name}.
                </InfoText>
            )}
            <PlanOptions data-testid="plan-options">
                {currentPlanType !== 'free' && renderCancelCard()}
                <PlanGroup data-testid="plan-group-whatsapp">
                    <PlanGroupHeader>
                        <PlanCardName>Assistente no WhatsApp</PlanCardName>
                        <PlanCardDescription>Assistente 24h no seu número</PlanCardDescription>
                        <PlanCardDescription>Lembretes e confirmações pelo WhatsApp</PlanCardDescription>
                        <PlanCardDescription>Cobrança de sinal</PlanCardDescription>
                    </PlanGroupHeader>
                    {PlanCatalogue.PAID_PLAN_IDS.map((id) => renderTierRow(PlanCatalogue.PAID[id]))}
                </PlanGroup>
            </PlanOptions>
            <Footnote>
                Profissionais ilimitados em todos os planos.{' '}
                {business && hasConnectorAccess(business) ? 'Na conexão oficial, a Meta' : 'A Meta'} cobra as mensagens
                do WhatsApp à parte, no cartão da sua conta Meta (~R$ 0,04 cada): lembretes e confirmações, e as
                respostas do assistente além das gratuitas do mês.
            </Footnote>

            {renderSimulationResult()}

            {!simulation && !isSamePlan && (
                <Footnote style={{ textAlign: 'center' }}>
                    Clique em &ldquo;Simular mudança&rdquo; para ver o que vai acontecer.
                </Footnote>
            )}
        </ModalSection>
    );

    if (!subscription) return null;

    const detailsFooter = (
        <>
            <Button onClick={close}>Fechar</Button>
            {!pendingTransition && (
                <Button primary onClick={handleStartChangePlan} data-testid="plan-change-button">
                    {isInactive ? 'Reativar' : 'Alterar plano'}
                </Button>
            )}
        </>
    );

    const changePlanFooter = (
        <>
            <Button onClick={handleBackToDetails} disabled={isSimulating || isChanging}>
                Voltar
            </Button>
            {simulation && simulation.type !== 'same_plan' ? (
                <Button primary onClick={handleConfirmChange} disabled={isChanging} data-testid="plan-confirm-button">
                    {isChanging ? 'Confirmando...' : 'Confirmar'}
                </Button>
            ) : (
                <Button
                    primary
                    onClick={handleSimulate}
                    disabled={isSimulating || isSamePlan}
                    data-testid="plan-simulate-button"
                >
                    {isSimulating ? 'Simulando...' : 'Simular mudança'}
                </Button>
            )}
        </>
    );

    return (
        <>
            <ConfigCard
                data-testid="subscription-card"
                onClick={() => {
                    gtagEvent('subscription_click');
                    open();
                }}
            >
                <ConfigInfo>
                    <ConfigTitle>Assinatura</ConfigTitle>
                    <ConfigSummary>
                        {getSummaryItems().map((item, idx) => (
                            <SummaryItem key={idx}>{item}</SummaryItem>
                        ))}
                    </ConfigSummary>
                </ConfigInfo>
            </ConfigCard>

            <Modal
                isOpen={isOpen}
                onClose={close}
                data-testid="plan-modal"
                title={showChangePlan ? 'Alterar plano' : 'Assinatura'}
                footer={showChangePlan ? changePlanFooter : detailsFooter}
            >
                {showChangePlan ? renderChangePlanView() : renderDetailsView()}
            </Modal>
        </>
    );
}
